import { useState, useCallback, useRef } from 'react';
import { useMutation } from 'react-apollo';
import { message } from 'antd';
import moment from 'moment-timezone';
import UPDATE_CAMPAIGN_ORDER_REQUESTOR from '../../../../core/GraphQl/Mutations/UPDATE_CAMPAIGN_ORDER_REQUESTOR';
import CREATE_CAMPAIGN_ORDER_REQUESTOR from '../../../../core/GraphQl/Mutations/CREATE_CAMPAIGN_ORDER_REQUESTOR';
import { campaignTypeMap } from '../../../../core/utils/constants/constants';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { getPricingFieldsForSave } from '../../../../core/utils/pricing/pricingFieldMapper';

// Configuration for sequential processing
const SEQUENTIAL_CONFIG = {
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  MAX_LINE_ITEM_PAYLOAD_SIZE: 1 * 1024 * 1024, // 1MB per line item
  CAMPAIGN_METADATA_MAX_SIZE: 10 * 1024, // 10KB for campaign metadata
  TIMEOUT_PER_LINE_ITEM: 30000, // 30 seconds per line item
  PROGRESS_UPDATE_INTERVAL: 100, // 100ms for UI updates
};

// Utility functions for date normalization
const normalizeStartDate = (date) => {
  if (!date) return null;
  return moment.isMoment(date) ? date : moment.utc(date);
};

const normalizeEndDate = (date) => {
  if (!date) return null;
  return moment.isMoment(date) ? date : moment.utc(date);
};

/**
 * Transform raw geoTarget data into the Prisma nested create format
 * Used for campaign-level geoTargets and attributionTargets
 */
const formatGeoTargetsForCreate = (geoTargets) => {
  if (!geoTargets || geoTargets.length === 0) {
    return undefined;
  }

  return {
    create: geoTargets.map((target) => ({
      name: target.name || '',
      start: target.start || null,
      end: target.end || null,
      status: 'CREATED',
      circles: {
        create: (target.circles || []).map((circle) => {
          const circlePayload = {
            radius: circle.radius,
            coordinate: {
              create: {
                lat: circle.coordinate?.lat || 0,
                lng: circle.coordinate?.lng || 0,
              },
            },
          };
          if (circle.location) {
            const { id, __typename, ...cleanLocation } = circle.location;
            circlePayload.location = {
              create: {
                name: cleanLocation.name || '',
                formattedAddress: cleanLocation.formattedAddress || '',
                zip: cleanLocation.zip || '',
                street: cleanLocation.street || '',
                city: cleanLocation.city || '',
                lat: cleanLocation.lat || 0,
                lng: cleanLocation.lng || 0,
                ...cleanLocation,
              },
            };
          }
          return circlePayload;
        }),
      },
      polygons: {
        create: (target.polygons || []).map((polygon) => {
          const polygonPayload = {
            geometry: {
              coordinates: polygon.geometry?.coordinates || [],
            },
          };
          if (polygon.location) {
            const { id, __typename, ...cleanLocation } = polygon.location;
            polygonPayload.location = {
              create: {
                name: cleanLocation.name || '',
                formattedAddress: cleanLocation.formattedAddress || '',
                zip: cleanLocation.zip || '',
                street: cleanLocation.street || '',
                city: cleanLocation.city || '',
                lat: cleanLocation.lat || 0,
                lng: cleanLocation.lng || 0,
                ...cleanLocation,
              },
            };
          }
          return polygonPayload;
        }),
      },
      location: target.location ? {
        create: {
          name: target.location.name || '',
          formattedAddress: target.location.formattedAddress || '',
          zip: target.location.zip || '',
          street: target.location.street || '',
          city: target.location.city || '',
          lat: target.location.lat || 0,
          lng: target.location.lng || 0,
          ...((({ id, __typename, ...rest }) => rest)(target.location)),
        },
      } : undefined,
    })),
  };
};

/**
 * Custom hook for managing campaign saves with sequential processing
 * Handles large campaigns by saving line items individually
 * Includes enhanced error recovery and granular progress tracking
 */
export const useCampaignSaveManager = () => {
  const { currentUserOrg } = useCampaignRequestorContext();

  const [saveProgress, setSaveProgress] = useState({
    isActive: false,
    phase: '',
    current: 0,
    total: 0,
    percent: 0,
    errors: [],
    currentOperation: '',
    estimatedTime: 0,
    // Enhanced tracking for sequential saves
    lineItemStatus: [], // Array of {id, name, status, error}
    failedLineItems: [], // Array of line items that failed
    completedLineItems: [], // Array of successfully saved line items
    currentLineItem: null, // Currently processing line item
    canRetry: false,
    isPaused: false,
  });

  const abortController = useRef(null);
  const startTime = useRef(null);

  const [createCampaignOrder] = useMutation(CREATE_CAMPAIGN_ORDER_REQUESTOR);
  const [updateCampaignOrder] = useMutation(UPDATE_CAMPAIGN_ORDER_REQUESTOR);

  /**
   * Update progress state with calculated percentage
   */
  const updateProgress = useCallback((updates) => {
    setSaveProgress((prev) => {
      const newState = {
        ...prev,
        ...updates,
      };

      // Calculate percentage if we have current and total (use existing total if not provided)
      const currentValue = updates.current !== undefined ? updates.current : newState.current;
      const totalValue = updates.total !== undefined ? updates.total : newState.total;

      if (currentValue !== undefined && totalValue !== undefined && totalValue > 0) {
        newState.percent = Math.round((currentValue / totalValue) * 100);
      }

      // Calculate estimated time remaining
      if (startTime.current && newState.current > 0 && newState.total > 0) {
        const elapsedMs = Date.now() - startTime.current;
        const avgTimePerItem = elapsedMs / newState.current;
        const remainingItems = newState.total - newState.current;
        newState.estimatedTime = Math.round((avgTimePerItem * remainingItems) / 1000);
      }

      return newState;
    });
  }, []);

  /**
   * Retry logic for failed mutations
   */
  const saveWithRetry = useCallback(async (mutationFn, variables, attemptNum = 1) => {


    try {
      const result = await mutationFn({ variables });
      return result;
    } catch (error) {
      console.error(`Mutation failed on attempt ${attemptNum}:`, error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      if (attemptNum < SEQUENTIAL_CONFIG.RETRY_ATTEMPTS) {
        // Check if the error is retryable
        const isRetryable =
          error.message?.includes('Payload too large') ||
          error.message?.includes('timeout') ||
          error.message?.includes('network');

        if (isRetryable) {
          await new Promise((resolve) =>
            setTimeout(resolve, SEQUENTIAL_CONFIG.RETRY_DELAY * attemptNum)
          );
          return saveWithRetry(mutationFn, variables, attemptNum + 1);
        }
      }
      throw error;
    }
  }, []);

  /**
   * Estimate the size of a JavaScript object in bytes
   */
  const estimatePayloadSize = (obj) => {
    const jsonString = JSON.stringify(obj);
    return new Blob([jsonString]).size;
  };

  /**
   * Extract only the essential campaign metadata for initial save
   * This creates the campaign order without any line items
   */
  const extractCampaignMetadata = useCallback((campaignData, currentCampaignOrder = null, selectedAdvertiser = null) => {
    const metadata = {
      name: campaignData.name,
      description: campaignData.description,
      startDate: normalizeStartDate(campaignData.startDate),
      endDate: normalizeEndDate(campaignData.endDate),
      budget: campaignData.budget,
      cpm: campaignData.cpm || 7,
      pricingModel: selectedAdvertiser?.pricingModel?.model || null,
      category: campaignData.category ? { set: campaignData.category } : undefined,
      advertiserType: campaignData.advertiserType,
      isRegulated: campaignData.isRegulated,
      type: campaignTypeMap[campaignData.type || 1],
      status: campaignData.status || 'DRAFT',
      kpi: campaignData.kpi || 'CPM',
      renewalStatus: campaignData.renewalStatus,
      notes: campaignData.notes,
      IONumber: campaignData.IONumber,
      productCode: campaignData.productCode || 'DIS',
      // Handle additionalIONumbers for new campaigns
      additionalIONumbers: !currentCampaignOrder && campaignData.additionalIONumbers && campaignData.additionalIONumbers.length > 0
        ? {
            create: campaignData.additionalIONumbers
              .filter(io => io.__isStaged) // Only create staged IO numbers
              .map(io => ({ value: io.value }))
          }
        : undefined,
      advertiser: campaignData.advertiser ? { connect: { id: campaignData.advertiser.id } } : undefined,
      awarenessCampaign: campaignData.awarenessCampaign,
      measureWalkInTraffic: campaignData.measureWalkInTraffic,
      transactionTarget: campaignData.transactionTarget,
      // Attribution configurations
      // Only include attributionTargets and geoTargets for CREATE operations
      // For UPDATE operations, these complex nested structures should be omitted unless explicitly modified
      // This prevents issues with GeoTargetUpdateManyInput schema validation
      attributionTargets: !currentCampaignOrder ? formatGeoTargetsForCreate(campaignData.attributionTargets) : undefined,
      geoTargets: !currentCampaignOrder ? formatGeoTargetsForCreate(campaignData.geoTargets) : undefined,
      // Connect to organizations - required for new campaigns
      orgs: !currentCampaignOrder && campaignData.advertiser && currentUserOrg ? {
        connect: [
          { id: currentUserOrg.id },
          { id: campaignData.advertiser.id },
        ],
      } : undefined,
      // Explicitly exclude line items from initial save
    };

    // Handle complianceAudiences connect/disconnect
    if (campaignData.complianceAudiences && Array.isArray(campaignData.complianceAudiences)) {
      const newComplianceIds = campaignData.complianceAudiences.map((a) => a.id);

      if (currentCampaignOrder) {
        // UPDATE: compute connect/disconnect
        const existingComplianceIds = (currentCampaignOrder.complianceAudiences || []).map((a) => a.id);
        const toConnect = newComplianceIds.filter((id) => !existingComplianceIds.includes(id));
        const toDisconnect = existingComplianceIds.filter((id) => !newComplianceIds.includes(id));

        const compliancePayload = {};
        if (toConnect.length > 0) {
          compliancePayload.connect = toConnect.map((id) => ({ id }));
        }
        if (toDisconnect.length > 0) {
          compliancePayload.disconnect = toDisconnect.map((id) => ({ id }));
        }
        if (Object.keys(compliancePayload).length > 0) {
          metadata.complianceAudiences = compliancePayload;
        }
      } else {
        // CREATE: connect all
        if (newComplianceIds.length > 0) {
          metadata.complianceAudiences = {
            connect: newComplianceIds.map((id) => ({ id })),
          };
        }
      }
    }

    // Handle transactionsConfig with proper GraphQL structure
    if (campaignData.transactionsConfig) {
      const transactionsConfig = campaignData.transactionsConfig;
      let appId = null;
      if (transactionsConfig.appId) {
        appId = typeof transactionsConfig.appId === 'string'
          ? transactionsConfig.appId
          : transactionsConfig.appId[0];
      }

      metadata.transactionTarget = appId;

      // Use raw cartProvider value without mapping
      const cartProvider = transactionsConfig.cartProvider;

      // Schema doesn't support upsert operations, use create for both new and existing campaigns
      metadata.transactionsConfig = {
        create: {
          appId: appId && appId.length ? appId : null,
          cartProvider: cartProvider,
          traffId: transactionsConfig.traffId,
          gtmAccess: transactionsConfig.gtmAccess,
          gtmStatus: transactionsConfig.gtmStatus,
          tracksBasketItems: transactionsConfig.tracksBasketItems,
          tag: transactionsConfig.tag,
        },
      };
    } else if (!campaignData.transactionsConfig && currentCampaignOrder && currentCampaignOrder.transactionsConfig) {
      // If transactionsConfig is removed and the current campaign order has one, delete it
      metadata.transactionsConfig = { delete: true };
      metadata.transactionTarget = null;
    } else {
      metadata.transactionsConfig = null;
    }

    // Handle signUpConfig with proper GraphQL structure
    if (campaignData.signUpConfig) {
      const signUpConfig = campaignData.signUpConfig;
      let appId = null;
      if (signUpConfig.appId) {
        appId = typeof signUpConfig.appId === 'string'
          ? signUpConfig.appId
          : signUpConfig.appId[0];
      }

      metadata.signUpCampaign = appId ? true : false;

      // Schema doesn't support upsert operations, use create operation
      metadata.signUpConfig = {
        create: {
          appId: appId,
          locationId: null,
          name: signUpConfig.tag ? signUpConfig.tag.name : '',
        },
      };
    } else if (!campaignData.signUpConfig && currentCampaignOrder && currentCampaignOrder.signUpConfig) {
      // If signUpConfig is removed and the current campaign order has one, delete it
      metadata.signUpConfig = { delete: true };
      metadata.signUpCampaign = false;
    } else {
      metadata.signUpConfig = null;
    }

    // Handle sessionConfig with proper GraphQL structure
    if (campaignData.sessionConfig) {
      const sessionConfig = campaignData.sessionConfig;
      const appId = typeof sessionConfig.appId === 'string'
        ? sessionConfig.appId
        : sessionConfig.appId?.[0];

      // Note: There is no sessionCampaign field in the schema, unlike signUpCampaign

      // Schema doesn't support upsert operations, use create operation
      // Only create sessionConfig if appId exists
      if (appId) {
        metadata.sessionConfig = {
          create: {
            appId: appId,
            isSession: true,
            cart: sessionConfig.cartProvider,
          },
        };
      } else {
        metadata.sessionConfig = null;
      }
    } else if (!campaignData.sessionConfig && currentCampaignOrder && currentCampaignOrder.sessionConfig) {
      // If sessionConfig is removed and the current campaign order has one, update it with null values
      // Following the original pattern which uses update instead of delete
      metadata.sessionConfig = {
        update: {
          isSession: false,
          appId: null,
          cart: null,
        },
      };
      // Note: There is no sessionCampaign field in the schema, unlike signUpCampaign
    } else {
      metadata.sessionConfig = null;
    }

    // Validate payload size
    const metadataSize = estimatePayloadSize(metadata);
    if (metadataSize > SEQUENTIAL_CONFIG.CAMPAIGN_METADATA_MAX_SIZE) {
      console.warn(`Campaign metadata size (${metadataSize} bytes) exceeds recommended limit`);
    }

    return metadata;
  }, [currentUserOrg]);

  /**
   * Prepare line item data for individual save
   * Each line item is saved independently with full data
   */
  const prepareLineItemForIndividualSave = useCallback((lineItem, _campaignId, _selectedAdvertiser, originalLineItems = [], isCreateContext = false, campaignOrderStatus = null) => {

    // Determine if this line item should be marked as origin
    // Line items are marked as isOrigin: true when:
    // 1. It's a new campaign order (no status yet), OR
    // 2. Campaign order status is DRAFT
    // AND the line item is being created (not updated)
    //
    // For retries or when isOrigin is already set on the line item, preserve that value
    let isOriginValue;
    if (isCreateContext) {
      // If the line item already has isOrigin set (e.g., from a retry), preserve it
      if (lineItem.isOrigin !== undefined) {
        isOriginValue = lineItem.isOrigin;
      } else {
        // New line item - set isOrigin based on campaign order status
        isOriginValue = !campaignOrderStatus || campaignOrderStatus === 'DRAFT';
      }
    } else {
      // Update context - preserve existing isOrigin value
      isOriginValue = lineItem.isOrigin;
    }

    // Create complete line item payload including all relationships
    // Note: campaign relationship is handled by the parent mutation structure
    const cleanLineItem = {
      name: lineItem.name || `Line Item ${Date.now()}`,
      startDate: normalizeStartDate(lineItem.startDate),
      endDate: normalizeEndDate(lineItem.endDate),
      budgetTotal: Number(lineItem.budgetTotal) || 0,
      cpm: Number(lineItem.cpm) || 7,
      targetingType: lineItem.targetingType,
      maxBid: lineItem.maxBid,
      frequencyCap: lineItem.frequencyCap,
      supplySidePlatform: lineItem.supplySidePlatform || null,
      valueAdd: lineItem.valueAdd,
      markedForDelivery: lineItem.markedForDelivery || false,
      isOrigin: isOriginValue,
      status: lineItem.status || null, // Include status for programmatic campaign updates
      // audienceType is added later with firstPartyDataAudiences
      // Don't include campaign field - it's handled by the nested structure
    };

    // Add targeting relationships - strategy depends on context
    const { targetingType } = lineItem;

    if (isCreateContext) {
      // In CREATE context: ALWAYS use create, ignore existing IDs (like buildLineItemPayload)
      switch (targetingType) {
        case 'REGION':
          if (lineItem.targetingRegions && Array.isArray(lineItem.targetingRegions) && lineItem.targetingRegions.length > 0) {
            // Apply deduplication in CREATE context as well to prevent duplicates
            const uniqueRegions = lineItem.targetingRegions.filter((region, index, array) =>
              array.findIndex(r => r.name === region.name) === index
            );


            cleanLineItem.targetingRegions = {
              create: uniqueRegions.map((region) => ({
                name: region.name,
                regions: {
                  set: (region.regions || []).map((r) => r),
                },
              }))
            };
          }
          break;

        case 'LOCAL':
          if (lineItem.targetingLocations && Array.isArray(lineItem.targetingLocations) && lineItem.targetingLocations.length > 0) {
            // Remove duplicates by name to prevent duplication
            const uniqueLocations = lineItem.targetingLocations.filter((location, index, array) =>
              array.findIndex(l => l.name === location.name) === index
            );


            cleanLineItem.targetingLocations = {
              create: uniqueLocations.map((location) => ({
                name: location.name,
                type: location.type,
                geoTargets: {
                  create: (location.geoTargets || []).map((target) => {
                  // Remove id and __typename from target

                  return {
                    name: target.name || '',
                    start: target.start || null,
                    end: target.end || null,
                    status: 'CREATED',
                    circles: {
                      create: (target.circles || []).map((circle) => {
                        // Remove id and __typename from circle
                        const circlePayload = {
                          radius: circle.radius,
                          coordinate: {
                            create: {
                              lat: circle.coordinate?.lat || 0,
                              lng: circle.coordinate?.lng || 0,
                            },
                          },
                        };

                        if (circle.location) {
                          const { id, __typename, ...cleanLocation } = circle.location;
                          circlePayload.location = {
                            create: {
                              name: cleanLocation.name || '',
                              formattedAddress: cleanLocation.formattedAddress || '',
                              zip: cleanLocation.zip || '',
                              street: cleanLocation.street || '',
                              city: cleanLocation.city || '',
                              lat: cleanLocation.lat || 0,
                              lng: cleanLocation.lng || 0,
                              ...cleanLocation,
                            },
                          };
                        }
                        return circlePayload;
                      })
                    },
                    polygons: {
                      create: (target.polygons || []).map((polygon) => {
                        // Remove id and __typename from polygon
                        const polygonPayload = {
                          geometry: {
                            coordinates: polygon.geometry?.coordinates || [],
                          },
                        };

                        if (polygon.location) {
                          const { id, __typename, ...cleanLocation } = polygon.location;
                          polygonPayload.location = {
                            create: {
                              name: cleanLocation.name || '',
                              formattedAddress: cleanLocation.formattedAddress || '',
                              zip: cleanLocation.zip || '',
                              street: cleanLocation.street || '',
                              city: cleanLocation.city || '',
                              lat: cleanLocation.lat || 0,
                              lng: cleanLocation.lng || 0,
                              ...cleanLocation,
                            },
                          };
                        }
                        return polygonPayload;
                      })
                    },
                    location: target.location ? {
                      create: {
                        name: target.location.name || '',
                        formattedAddress: target.location.formattedAddress || '',
                        zip: target.location.zip || '',
                        street: target.location.street || '',
                        city: target.location.city || '',
                        lat: target.location.lat || 0,
                        lng: target.location.lng || 0,
                        ...((({ id, __typename, ...rest }) => rest)(target.location)),
                      },
                    } : undefined,
                  };
                })
              }
            }))
          };
        }
        break;

        case 'COUNTRY':
          if (lineItem.targetingCountries) {
            const countryMap = {
              'US': 'United States',
              'CA': 'Canada',
            };

            const newTargetingCountries = Array.isArray(lineItem.targetingCountries)
              ? lineItem.targetingCountries.map(country => ({
                  name: typeof country === 'string' ? (countryMap[country] || country) : country.name,
                  code: typeof country === 'string' ? country : country.code,
                }))
              : [{
                  name: countryMap[lineItem.targetingCountries] || lineItem.targetingCountries,
                  code: lineItem.targetingCountries,
                }];

            cleanLineItem.targetingCountries = {
              create: newTargetingCountries
            };
          }
          break;

        default:
          break;
      }
    } else {
      // In UPDATE context: Use create/update/delete logic (like buildLineItemUpdatePayload)
      switch (targetingType) {
        case 'REGION':
          if (lineItem.targetingRegions && Array.isArray(lineItem.targetingRegions) && lineItem.targetingRegions.length > 0) {
            // Check if lineItem has an ID to determine if we're in update context
            if (lineItem.id && !isCreateContext) {
              // UPDATE context: use proper create/update/delete operations
              const targetingRegionsPayload = {
                create: [],
                update: [],
                delete: []
              };

              const originalLineItem = originalLineItems?.find((original) => original.id === lineItem.id);
              const originalTargetingRegions = originalLineItem?.targetingRegions || [];

              // Find regions to delete (in original but not in current)
              const currentRegionNames = new Set(lineItem.targetingRegions.map(r => r.name));
              const regionsToDelete = originalTargetingRegions.filter(orig => !currentRegionNames.has(orig.name));

              if (regionsToDelete.length > 0) {
                targetingRegionsPayload.delete = regionsToDelete.map(region => ({ id: region.id }));
              }

              // Find regions to create (new ones)
              const originalRegionNames = new Set(originalTargetingRegions.map(r => r.name));
              const regionsToCreate = lineItem.targetingRegions.filter(curr => !originalRegionNames.has(curr.name));

              if (regionsToCreate.length > 0) {
                targetingRegionsPayload.create = regionsToCreate.map(region => ({
                  name: region.name,
                  regions: {
                    set: (region.regions || []).map((r) => r),
                  },
                }));
              }

              // For now, we're not handling updates (regions with same name but different content)
              // since the current UI doesn't seem to support editing existing regions

              cleanLineItem.targetingRegions = targetingRegionsPayload;
            } else {
              // CREATE context: only create operations allowed
              cleanLineItem.targetingRegions = {
                create: lineItem.targetingRegions.map((region) => ({
                  name: region.name,
                  regions: {
                    set: (region.regions || []).map((r) => r),
                  },
                }))
              };
            }
          } else {
            // If no regions are selected, we still need to delete existing ones in update context
            if (lineItem.id && !isCreateContext) {
              const originalLineItem = originalLineItems?.find((original) => original.id === lineItem.id);
              if (originalLineItem && originalLineItem.targetingRegions && originalLineItem.targetingRegions.length > 0) {
                cleanLineItem.targetingRegions = {
                  delete: originalLineItem.targetingRegions.map((region) => ({
                    id: region.id,
                  }))
                };
              }
            }
          }
          break;

        case 'LOCAL':
          if (lineItem.targetingLocations && Array.isArray(lineItem.targetingLocations) && lineItem.targetingLocations.length > 0) {
            // Check if lineItem has an ID to determine if we're in update context
            if (lineItem.id && !isCreateContext) {
              // UPDATE context: use proper create/update/delete operations
              const targetingLocationsPayload = {
                create: [],
                update: [],
                delete: []
              };

              const originalLineItem = originalLineItems?.find((original) => original.id === lineItem.id);
              const originalTargetingLocations = originalLineItem?.targetingLocations || [];

              // Find locations to delete (in original but not in current)
              const currentLocationNames = new Set(lineItem.targetingLocations.map(l => l.name));
              const locationsToDelete = originalTargetingLocations.filter(orig => !currentLocationNames.has(orig.name));

              if (locationsToDelete.length > 0) {
                targetingLocationsPayload.delete = locationsToDelete.map(location => ({ id: location.id }));
              }

              // Find locations to create (new ones)
              const originalLocationNames = new Set(originalTargetingLocations.map(l => l.name));
              const locationsToCreate = lineItem.targetingLocations.filter(curr => !originalLocationNames.has(curr.name));

              if (locationsToCreate.length > 0) {
                targetingLocationsPayload.create = locationsToCreate.map((location) => ({
                  name: location.name,
                  type: location.type,
                  geoTargets: {
                    create: (location.geoTargets || []).map((target) => ({
                      name: target.name || '',
                      start: target.start || null,
                      end: target.end || null,
                      status: 'CREATED',
                      // Handle circles
                      circles: target.circles && Array.isArray(target.circles) ? {
                        create: target.circles.map((circle) => ({
                          radius: circle.radius || 0,
                          coordinate: circle.coordinate ? {
                            create: {
                              lat: circle.coordinate.lat || 0,
                              lng: circle.coordinate.lng || 0,
                            }
                          } : undefined,
                        }))
                      } : undefined,
                      // Handle polygons
                      polygons: target.polygons && Array.isArray(target.polygons) ? {
                        create: target.polygons.map((polygon) => ({
                          name: polygon.name || '',
                          strokeColor: polygon.strokeColor || '',
                          strokeOpacity: polygon.strokeOpacity || 0,
                          strokeWeight: polygon.strokeWeight || 0,
                          fillColor: polygon.fillColor || '',
                          fillOpacity: polygon.fillOpacity || 0,
                          type: polygon.type || 'POLYGON',
                          lat: polygon.lat || 0,
                          lng: polygon.lng || 0,
                        }))
                      } : undefined,
                      // Handle location
                      location: target.location ? {
                        create: {
                          name: target.location.name || '',
                          formattedAddress: target.location.formattedAddress || '',
                          zip: target.location.zip || '',
                          street: target.location.street || '',
                          city: target.location.city || '',
                          state: target.location.state || '',
                          lat: target.location.lat || 0,
                          lng: target.location.lng || 0,
                          country: target.location.country || '',
                          county: target.location.county || null,
                        },
                      } : undefined,
                    }))
                  }
                }));
              }

              // For now, we're not handling updates (locations with same name but different content)
              // since the current UI doesn't seem to support editing existing locations

              cleanLineItem.targetingLocations = targetingLocationsPayload;
            } else {
              // CREATE context: only create operations allowed
              cleanLineItem.targetingLocations = {
                create: lineItem.targetingLocations.map((location) => ({
                  name: location.name,
                  type: location.type,
                  geoTargets: {
                    create: (location.geoTargets || []).map((target) => ({
                      name: target.name || '',
                      start: target.start || null,
                      end: target.end || null,
                      status: 'CREATED',
                      // Handle circles
                      circles: target.circles && Array.isArray(target.circles) ? {
                        create: target.circles.map((circle) => ({
                          radius: circle.radius || 0,
                          coordinate: circle.coordinate ? {
                            create: {
                              lat: circle.coordinate.lat || 0,
                              lng: circle.coordinate.lng || 0,
                            }
                          } : undefined,
                        }))
                      } : undefined,
                      // Handle polygons
                      polygons: target.polygons && Array.isArray(target.polygons) ? {
                        create: target.polygons.map((polygon) => ({
                          name: polygon.name || '',
                          strokeColor: polygon.strokeColor || '',
                          strokeOpacity: polygon.strokeOpacity || 0,
                          strokeWeight: polygon.strokeWeight || 0,
                          fillColor: polygon.fillColor || '',
                          fillOpacity: polygon.fillOpacity || 0,
                          type: polygon.type || 'POLYGON',
                          lat: polygon.lat || 0,
                          lng: polygon.lng || 0,
                        }))
                      } : undefined,
                      // Handle location
                      location: target.location ? {
                        create: {
                          name: target.location.name || '',
                          formattedAddress: target.location.formattedAddress || '',
                          zip: target.location.zip || '',
                          street: target.location.street || '',
                          city: target.location.city || '',
                          state: target.location.state || '',
                          lat: target.location.lat || 0,
                          lng: target.location.lng || 0,
                          country: target.location.country || '',
                          county: target.location.county || null,
                        },
                      } : undefined,
                    }))
                  }
                }))
              };
            }
          } else {
            // If no locations are selected, we still need to delete existing ones in update context
            if (lineItem.id && !isCreateContext) {
              const originalLineItem = originalLineItems?.find((original) => original.id === lineItem.id);
              if (originalLineItem && originalLineItem.targetingLocations && originalLineItem.targetingLocations.length > 0) {
                cleanLineItem.targetingLocations = {
                  delete: originalLineItem.targetingLocations.map((location) => ({
                    id: location.id,
                  }))
                };
              }
            }
          }
          break;

        case 'COUNTRY':
          if (lineItem.targetingCountries) {
            const countryMap = {
              'US': 'United States',
              'CA': 'Canada',
            };

            const newTargetingCountries = Array.isArray(lineItem.targetingCountries)
              ? lineItem.targetingCountries.map(country => ({
                  name: typeof country === 'string' ? (countryMap[country] || country) : country.name,
                  code: typeof country === 'string' ? country : country.code,
                }))
              : [{
                  name: countryMap[lineItem.targetingCountries] || lineItem.targetingCountries,
                  code: lineItem.targetingCountries,
                }];

            cleanLineItem.targetingCountries = {
              create: newTargetingCountries
            };
          }

          // Handle deletion of regions and locations when switching to COUNTRY (following old implementation pattern)
          if (originalLineItems && originalLineItems.length > 0) {
            const originalLineItem = originalLineItems.find((original) => original.id === lineItem.id);
            if (originalLineItem) {
              // Note: GraphQL schema doesn't support delete operations for targetingRegions
              if (originalLineItem.targetingRegions && originalLineItem.targetingRegions.length > 0) {
                // No delete operation - regions will remain but won't be used for COUNTRY targeting
              }
              // Note: AudienceCreateManyInput only supports create operations, not delete
              // When switching to COUNTRY, we simply don't include targeting locations
              if (originalLineItem.targetingLocations && originalLineItem.targetingLocations.length > 0) {
                // No delete operation needed since we're not including targetingLocations in the payload
              }
              // Delete all existing targeting countries when switching country selection
              if (originalLineItem.targetingCountries && originalLineItem.targetingCountries.length > 0) {
                if (!cleanLineItem.targetingCountries) {
                  cleanLineItem.targetingCountries = {};
                }
                // Note: Delete operations not supported - countries will be replaced by new create operations
              }
            }
          }
          break;

        default:
          break;
      }
    }

    // Handle audienceType and firstPartyDataAudiences (following original logic exactly)
    cleanLineItem.audienceType = lineItem.audienceType;

    // Handle audience type specific logic (from original buildLineItemPayload)
    if (isCreateContext) {
      // In CREATE context: Simple create logic
      switch (lineItem.audienceType) {
        case 'FIRST_PARTY_DATA':
          if (lineItem.firstPartyDataAudiences && Array.isArray(lineItem.firstPartyDataAudiences)) {
            cleanLineItem.firstPartyDataAudiences = {
              create: lineItem.firstPartyDataAudiences.map((audience) => ({
                name: audience.name,
                activated: audience.activated,
                tag: audience.tag,
                deviceCount: audience.deviceCount,
                matchRate: audience.matchRate,
                isProcessedByStatera: audience.isProcessedByStatera,
                dspSegmentIds: {
                  set: audience.dspSegmentIds || [],
                },
              }))
            };
          }
          break;
        case 'ALL_USERS':
        default:
          cleanLineItem.firstPartyDataAudiences = {
            create: [],
          };
          break;
      }
    } else {
      // In UPDATE context: Handle create/update/delete logic like buildLineItemUpdatePayload
      switch (lineItem.audienceType) {
        case 'FIRST_PARTY_DATA':
          if (lineItem.firstPartyDataAudiences && Array.isArray(lineItem.firstPartyDataAudiences)) {
            // Separate new audiences (without IDs) from existing audiences (with IDs)
            const newFirstPartyDataAudiences = lineItem.firstPartyDataAudiences.filter((audience) => !audience.id);
            const existingFirstPartyDataAudiences = lineItem.firstPartyDataAudiences.filter((audience) => audience.id);

            const firstPartyAudiencesPayload = {};

            // Only create NEW audiences (ones without IDs) to avoid duplication
            if (newFirstPartyDataAudiences.length > 0) {
              firstPartyAudiencesPayload.create = newFirstPartyDataAudiences.map((audience) => ({
                name: audience.name,
                activated: audience.activated,
                tag: audience.tag,
                deviceCount: audience.deviceCount,
                matchRate: audience.matchRate,
                isProcessedByStatera: audience.isProcessedByStatera,
                dspSegmentIds: {
                  set: audience.dspSegmentIds || [],
                },
              }));
            }

            // Handle deletion: Find audiences that were in original but not in current
            if (originalLineItems && originalLineItems.length > 0) {
              const originalLineItem = originalLineItems.find((original) => original.id === lineItem.id);
              if (originalLineItem && originalLineItem.firstPartyDataAudiences) {
                const currentAudienceIds = new Set(existingFirstPartyDataAudiences.map(a => a.id));
                const audiencesToDelete = originalLineItem.firstPartyDataAudiences.filter(
                  orig => !currentAudienceIds.has(orig.id)
                );

                if (audiencesToDelete.length > 0) {
                  // If schema supports delete, add delete operations
                  // Otherwise we can't delete due to schema limitations
                  // Uncomment if schema supports delete:
                  // firstPartyAudiencesPayload.delete = audiencesToDelete.map(a => ({ id: a.id }));
                }
              }
            }

            // Note: Existing audiences with IDs are NOT recreated - they already exist!

            if (Object.keys(firstPartyAudiencesPayload).length > 0) {
              cleanLineItem.firstPartyDataAudiences = firstPartyAudiencesPayload;
            }
          } else {
            cleanLineItem.firstPartyDataAudiences = {
              create: [],
            };
          }
          break;

        case 'ALL_USERS':
        default:
          // Delete all first party data audiences when switching to ALL_USERS
          cleanLineItem.firstPartyDataAudiences = {
            create: [],
          };

          if (originalLineItems && originalLineItems.length > 0) {
            const originalLineItem = originalLineItems.find((original) => original.id === lineItem.id);
            if (originalLineItem && originalLineItem.firstPartyDataAudiences && originalLineItem.firstPartyDataAudiences.length > 0) {
              cleanLineItem.firstPartyDataAudiences.delete = originalLineItem.firstPartyDataAudiences.map((audience) => ({
                id: audience.id,
              }));
            }
          }
          break;
      }
    }

    // Add ad group - strategy depends on context
    if (lineItem.adGroup) {
      if (isCreateContext) {
        // In CREATE context: Use simple create structure (like buildLineItemPayload)
        cleanLineItem.adGroup = {
          create: {
            adGroupCreatives: {
              create: (lineItem.adGroup.adGroupCreatives || []).map(creative => ({
                name: creative.name,
                clickthrough: creative.clickthrough,
                startDate: creative.startDate,
                endDate: creative.endDate,
                medias: creative.medias && Array.isArray(creative.medias) ? {
                  create: creative.medias.map(mediaItem => ({
                    fileType: mediaItem.fileType,
                    height: mediaItem.height,
                    key: mediaItem.key,
                    mediaHasNotes: mediaItem.mediaHasNotes,
                    mediaNotes: mediaItem.mediaNotes,
                    name: mediaItem.name,
                    org: mediaItem.org,
                    size: mediaItem.size,
                    type: mediaItem.type,
                    url: mediaItem.url,
                    width: mediaItem.width,
                    status: mediaItem.status,
                    sharedWith: mediaItem.sharedWith ? {
                      connect: {
                        id: mediaItem.sharedWith.id || currentUserOrg?.id,
                      },
                    } : undefined,
                  }))
                } : undefined,
              }))
            }
          }
        };
      } else {
        // Note: LineItemAdGroupCreateOneInput only supports 'create', not 'update'
        // So we'll always use create structure even in UPDATE context
        cleanLineItem.adGroup = {
          create: {
            adGroupCreatives: {
              create: (lineItem.adGroup.adGroupCreatives || []).map(creative => ({
                name: creative.name,
                clickthrough: creative.clickthrough,
                startDate: creative.startDate,
                endDate: creative.endDate,
                medias: creative.medias && Array.isArray(creative.medias) ? {
                  create: creative.medias.map(mediaItem => ({
                    fileType: mediaItem.fileType,
                    height: mediaItem.height,
                    key: mediaItem.key,
                    mediaHasNotes: mediaItem.mediaHasNotes,
                    mediaNotes: mediaItem.mediaNotes,
                    name: mediaItem.name,
                    org: mediaItem.org,
                    size: mediaItem.size,
                    type: mediaItem.type,
                    url: mediaItem.url,
                    width: mediaItem.width,
                    status: mediaItem.status,
                    sharedWith: mediaItem.sharedWith ? {
                      connect: {
                        id: mediaItem.sharedWith.id || currentUserOrg?.id,
                      },
                    } : undefined,
                  }))
                } : undefined,
              }))
            }
          }
        };
      }
    }

    // Add adUnits (was missing from optimized version)
    cleanLineItem.adUnits = {
      create: [],
    };

    // Add flight schedules handling
    if (lineItem.flightSchedule && Array.isArray(lineItem.flightSchedule) && lineItem.flightSchedule.length > 0) {
      // Separate new flights (without IDs) from existing flights (with IDs)
      const newFlights = lineItem.flightSchedule.filter((flight) => !flight.id);
      const existingFlights = lineItem.flightSchedule.filter((flight) => flight.id);

      const flightSchedulesPayload = {};

      // Only create NEW flights (ones without IDs) to avoid duplication
      if (newFlights.length > 0) {
        flightSchedulesPayload.create = newFlights.map((flight) => ({
          startDate: normalizeStartDate(flight.startDate),
          endDate: normalizeEndDate(flight.endDate),
          budget: Number(flight.budget) || 0,
          cpm: Number(flight.cpm) || 7,
        }));
      }

      // Handle updates and deletions: Compare with original flights
      if (originalLineItems && originalLineItems.length > 0) {
        const originalLineItem = originalLineItems.find((original) => original.id === lineItem.id);
        if (originalLineItem && originalLineItem.flightSchedules) {
          const currentFlightIds = new Set(existingFlights.map(f => f.id));

          // Find flights to delete (in original but not in current)
          const flightsToDelete = originalLineItem.flightSchedules.filter(
            orig => !currentFlightIds.has(orig.id)
          );

          if (flightsToDelete.length > 0) {
            flightSchedulesPayload.delete = flightsToDelete.map(f => ({ id: f.id }));
          }

          // Find flights to update (existing flights that have been modified)
          const flightsToUpdate = [];
          existingFlights.forEach((currentFlight) => {
            const originalFlight = originalLineItem.flightSchedules.find(orig => orig.id === currentFlight.id);
            if (originalFlight) {
              // Check if any field has changed
              const hasChanged =
                normalizeStartDate(currentFlight.startDate) !== normalizeStartDate(originalFlight.startDate) ||
                normalizeEndDate(currentFlight.endDate) !== normalizeEndDate(originalFlight.endDate) ||
                Number(currentFlight.budget) !== Number(originalFlight.budget) ||
                Number(currentFlight.cpm) !== Number(originalFlight.cpm);

              if (hasChanged) {
                flightsToUpdate.push({
                  where: { id: currentFlight.id },
                  data: {
                    startDate: normalizeStartDate(currentFlight.startDate),
                    endDate: normalizeEndDate(currentFlight.endDate),
                    budget: Number(currentFlight.budget) || 0,
                    cpm: Number(currentFlight.cpm) || 7,
                  },
                });
              }
            }
          });

          if (flightsToUpdate.length > 0) {
            flightSchedulesPayload.update = flightsToUpdate;
          }
        }
      }

      if (Object.keys(flightSchedulesPayload).length > 0) {
        cleanLineItem.flightSchedules = flightSchedulesPayload;
      }
    } else {
      // If no flight schedules in form, delete all existing ones if in update mode
      if (!isCreateContext && originalLineItems && originalLineItems.length > 0) {
        const originalLineItem = originalLineItems.find((original) => original.id === lineItem.id);
        if (originalLineItem && originalLineItem.flightSchedules && originalLineItem.flightSchedules.length > 0) {
          cleanLineItem.flightSchedules = {
            delete: originalLineItem.flightSchedules.map((flight) => ({
              id: flight.id,
            })),
          };
        }
      }
    }

    // Add Deal IDs handling
    if (lineItem.dealIds && Array.isArray(lineItem.dealIds) && lineItem.dealIds.length > 0) {
      if (isCreateContext) {
        // In CREATE context: connect to existing deal IDs
        cleanLineItem.dealIds = {
          connect: lineItem.dealIds.map((deal) => ({ id: deal.id })),
        };
      } else {
        // In UPDATE context: handle connect/disconnect operations
        const originalLineItem = originalLineItems?.find((original) => original.id === lineItem.id);
        const originalDealIds = originalLineItem?.dealIds || [];

        // Find new deal IDs (in current but not in original)
        const currentDealIdSet = new Set(lineItem.dealIds.map(d => d.id));
        const originalDealIdSet = new Set(originalDealIds.map(d => d.id));

        const dealIdsToConnect = lineItem.dealIds.filter(deal => !originalDealIdSet.has(deal.id));
        const dealIdsToDisconnect = originalDealIds.filter(deal => !currentDealIdSet.has(deal.id));

        const dealIdsPayload = {};
        if (dealIdsToConnect.length > 0) {
          dealIdsPayload.connect = dealIdsToConnect.map((deal) => ({ id: deal.id }));
        }
        if (dealIdsToDisconnect.length > 0) {
          dealIdsPayload.disconnect = dealIdsToDisconnect.map((deal) => ({ id: deal.id }));
        }

        if (Object.keys(dealIdsPayload).length > 0) {
          cleanLineItem.dealIds = dealIdsPayload;
        }
      }
    } else {
      // If no deal IDs in form, disconnect all existing ones if in update mode
      if (!isCreateContext && originalLineItems && originalLineItems.length > 0) {
        const originalLineItem = originalLineItems.find((original) => original.id === lineItem.id);
        if (originalLineItem && originalLineItem.dealIds && originalLineItem.dealIds.length > 0) {
          cleanLineItem.dealIds = {
            disconnect: originalLineItem.dealIds.map((deal) => ({ id: deal.id })),
          };
        }
      }
    }

    // DEBUG: Log final payload structure

    // Special debug for deletions
    if (cleanLineItem.targetingRegions && cleanLineItem.targetingRegions.delete) {

      // Alert user to confirm deletion is being processed
      console.warn('⚠️  REGION DELETION DETECTED - Will delete:',
        cleanLineItem.targetingRegions.delete.map(d => d.id).join(', '));
    }


    // Validate payload size
    const lineItemSize = estimatePayloadSize(cleanLineItem);
    if (lineItemSize > SEQUENTIAL_CONFIG.MAX_LINE_ITEM_PAYLOAD_SIZE) {
      console.warn(`Line item "${lineItem.name}" payload size (${lineItemSize} bytes) exceeds limit`);
      // TODO: Implement creative batching for oversized line items
    }

    return cleanLineItem;
  }, [currentUserOrg.id]);

  /**
   * Initialize line item status tracking
   */
  const initializeLineItemStatus = (lineItems, campaignData) => {
    // Safety check: ensure lineItems is an array
    if (!lineItems || !Array.isArray(lineItems)) {
      return [];
    }

    return lineItems.map((lineItem, index) => {
      // Get campaign name from campaign index
      const campaignIndex = lineItem._campaignIndex ?? 0;
      const campaign = campaignData?.campaigns?.[campaignIndex];
      const campaignName = campaign?.name || `Campaign ${campaignIndex + 1}`;

      return {
        id: lineItem.id || `temp-${index}`,
        name: lineItem.name || `Line Item ${index + 1}`,
        campaignName: campaignName,
        status: 'pending', // pending, processing, completed, failed
        error: null,
        startTime: null,
        endTime: null,
        index: index,
      };
    });
  };

  /**
   * Update individual line item status
   */
  const updateLineItemStatus = useCallback((lineItemId, status, error = null) => {
    setSaveProgress(prev => {
      const updatedStatus = prev.lineItemStatus.map(item => {
        if (item.id === lineItemId) {
          const updatedItem = {
            ...item,
            status,
            error,
            endTime: status === 'completed' || status === 'failed' ? Date.now() : null,
          };
          if (status === 'processing' && !item.startTime) {
            updatedItem.startTime = Date.now();
          }
          return updatedItem;
        }
        return item;
      });

      // Update progress counters
      const completed = updatedStatus.filter(item => item.status === 'completed').length;
      const failed = updatedStatus.filter(item => item.status === 'failed').length;
      const processing = updatedStatus.filter(item => item.status === 'processing').length;

      return {
        ...prev,
        lineItemStatus: updatedStatus,
        current: completed + failed,
        completedLineItems: updatedStatus.filter(item => item.status === 'completed'),
        failedLineItems: updatedStatus.filter(item => item.status === 'failed'),
        currentLineItem: processing > 0 ? updatedStatus.find(item => item.status === 'processing') : null,
        canRetry: failed > 0,
      };
    });
  }, []);

  /**
   * Main save function for campaigns with sequential processing
   * Saves campaign order first, then processes line items individually
   */
  const saveCampaignSequentially = useCallback(
    async (campaignData, isUpdate = false, currentCampaignOrder = null, selectedAdvertiser = null) => {
      abortController.current = new AbortController();
      startTime.current = Date.now();

      try {
        // Collect ALL line items from ALL campaigns with campaign tracking
        const lineItemsWithCampaignIndex = [];
        if (campaignData.campaigns && Array.isArray(campaignData.campaigns)) {
          campaignData.campaigns.forEach((campaign, campaignIndex) => {
            if (campaign.lineItems && Array.isArray(campaign.lineItems)) {
              campaign.lineItems.forEach((lineItem) => {
                lineItemsWithCampaignIndex.push({
                  ...lineItem,
                  _campaignIndex: campaignIndex, // Track which campaign this line item belongs to
                });
              });
            }
          });
        }

        const lineItems = lineItemsWithCampaignIndex.length > 0
          ? lineItemsWithCampaignIndex
          : (campaignData.campaigns || []); // Fallback for old format
        const totalSteps = lineItems.length + 2; // +2 for campaign creation and completion

        // Initialize line item tracking
        const lineItemStatus = initializeLineItemStatus(lineItems, campaignData);

        updateProgress({
          isActive: true,
          phase: isUpdate ? 'Updating campaign order' : 'Creating campaign order',
          currentOperation: 'Saving campaign metadata...',
          current: 0,
          total: totalSteps,
          lineItemStatus,
          errors: [],
          failedLineItems: [],
          completedLineItems: [],
          currentLineItem: null,
          canRetry: false,
          isPaused: false,
        });

        let campaignOrderId;
        let parentCampaignId;
        let campaignIdsByIndex = {}; // Map campaign index to campaign ID

        // Phase 1: Create/Update Campaign Order (without line items)
        if (isUpdate && currentCampaignOrder) {
          campaignOrderId = currentCampaignOrder.id;

          const updateData = extractCampaignMetadata(campaignData, currentCampaignOrder, selectedAdvertiser);

          // Handle campaign updates, deletions, and creations
          if (currentCampaignOrder.campaigns && currentCampaignOrder.campaigns.length > 0) {
            // Identify campaigns to delete (exist in database but not in form)
            const existingCampaignIds = currentCampaignOrder.campaigns.map(c => c.id);
            const formCampaignIds = (campaignData.campaigns || [])
              .filter(c => c.id) // Only existing campaigns have IDs
              .map(c => c.id);
            const campaignsToDelete = existingCampaignIds.filter(id => !formCampaignIds.includes(id));

            // Build campaigns update payload
            const campaignsUpdatePayload = {};

            // Handle updates for existing campaigns
            const campaignsToUpdate = (campaignData.campaigns || []).filter(c => c.id);
            if (campaignsToUpdate.length > 0) {
              campaignsUpdatePayload.update = campaignsToUpdate.map((campaignFromForm) => {
                const existingCampaign = currentCampaignOrder.campaigns.find(c => c.id === campaignFromForm.id);

                // Identify line items to delete for this campaign
                const existingLineItemIds = (existingCampaign?.lineItems || []).map(li => li.id);
                const formLineItemIds = (campaignFromForm.lineItems || [])
                  .filter(li => li.id) // Only existing line items have IDs
                  .map(li => li.id);
                const lineItemsToDelete = existingLineItemIds.filter(id => !formLineItemIds.includes(id));

                // Build line items payload if there are deletions
                const lineItemsPayload = {};
                if (lineItemsToDelete.length > 0) {
                  lineItemsPayload.delete = lineItemsToDelete.map(id => ({ id }));
                }

                return {
                  where: { id: campaignFromForm.id },
                  data: {
                    name: campaignFromForm?.name || existingCampaign?.name || 'Campaign',
                    budgetTotal: Number(campaignFromForm?.budget || existingCampaign?.budgetTotal || 0),
                    startDate: normalizeStartDate(campaignFromForm?.startDate || existingCampaign?.startDate),
                    endDate: normalizeEndDate(campaignFromForm?.endDate || existingCampaign?.endDate),
                    ...getPricingFieldsForSave(
                      campaignFromForm?.cpm || existingCampaign?.cpm || 7,
                      selectedAdvertiser,
                      campaignFromForm?.type || existingCampaign?.type || 1,
                      campaignFromForm?.advertiserPercentage || existingCampaign?.advertiserPercentage
                    ),
                    type: campaignTypeMap[campaignFromForm?.type || existingCampaign?.type || 1],
                    hasMediaPlanStrategy: campaignFromForm?.hasMediaPlanStrategy ?? existingCampaign?.hasMediaPlanStrategy ?? false,
                    valueAdd: campaignFromForm?.valueAdd ?? existingCampaign?.valueAdd ?? false,
                    demandSidePlatform: campaignFromForm?.demandSidePlatform || existingCampaign?.demandSidePlatform || null,
                    adProviderId: campaignFromForm?.adProviderId || existingCampaign?.adProviderId || null,
                    maxBid: campaignFromForm?.maxBid ? Number(campaignFromForm.maxBid) : (existingCampaign?.maxBid || null),
                    // Campaign-level flight schedules CRUD
                    ...(() => {
                      const formFlights = campaignFromForm?.flightSchedule || [];
                      const originalFlights = existingCampaign?.flightSchedules || [];

                      if (formFlights.length > 0) {
                        const newFlights = formFlights.filter((f) => !f.id);
                        const existingFlights = formFlights.filter((f) => f.id);
                        const flightSchedulesPayload = {};

                        // Create new flights
                        if (newFlights.length > 0) {
                          flightSchedulesPayload.create = newFlights.map((f) => ({
                            startDate: normalizeStartDate(f.startDate),
                            endDate: normalizeEndDate(f.endDate),
                            budget: Number(f.budget) || 0,
                            cpm: Number(f.cpm) || 7,
                          }));
                        }

                        // Delete removed flights
                        const currentFlightIds = new Set(existingFlights.map(f => f.id));
                        const flightsToDelete = originalFlights.filter(orig => !currentFlightIds.has(orig.id));
                        if (flightsToDelete.length > 0) {
                          flightSchedulesPayload.delete = flightsToDelete.map(f => ({ id: f.id }));
                        }

                        // Update modified flights
                        const flightsToUpdate = [];
                        existingFlights.forEach((currentFlight) => {
                          const originalFlight = originalFlights.find(orig => orig.id === currentFlight.id);
                          if (originalFlight) {
                            const hasChanged =
                              normalizeStartDate(currentFlight.startDate)?.format() !== normalizeStartDate(originalFlight.startDate)?.format() ||
                              normalizeEndDate(currentFlight.endDate)?.format() !== normalizeEndDate(originalFlight.endDate)?.format() ||
                              Number(currentFlight.budget) !== Number(originalFlight.budget) ||
                              Number(currentFlight.cpm) !== Number(originalFlight.cpm);

                            if (hasChanged) {
                              flightsToUpdate.push({
                                where: { id: currentFlight.id },
                                data: {
                                  startDate: normalizeStartDate(currentFlight.startDate),
                                  endDate: normalizeEndDate(currentFlight.endDate),
                                  budget: Number(currentFlight.budget) || 0,
                                  cpm: Number(currentFlight.cpm) || 7,
                                },
                              });
                            }
                          }
                        });

                        if (flightsToUpdate.length > 0) {
                          flightSchedulesPayload.update = flightsToUpdate;
                        }

                        return Object.keys(flightSchedulesPayload).length > 0
                          ? { flightSchedules: flightSchedulesPayload }
                          : {};
                      } else if (originalFlights.length > 0) {
                        // All flight schedules removed — delete them all
                        return {
                          flightSchedules: {
                            delete: originalFlights.map((f) => ({ id: f.id })),
                          },
                        };
                      }
                      return {};
                    })(),
                    // Add line items delete operation if needed
                    ...(Object.keys(lineItemsPayload).length > 0 ? { lineItems: lineItemsPayload } : {}),
                  }
                };
              });
            }

            // Handle creations for new campaigns (campaigns without IDs)
            const campaignsToCreate = (campaignData.campaigns || []).filter(c => !c.id);
            if (campaignsToCreate.length > 0) {
              campaignsUpdatePayload.create = campaignsToCreate.map((campaignFromForm) => ({
                name: campaignFromForm?.name || 'Campaign',
                budgetTotal: Number(campaignFromForm?.budget || 0),
                startDate: normalizeStartDate(campaignFromForm?.startDate || campaignData.startDate),
                endDate: normalizeEndDate(campaignFromForm?.endDate || campaignData.endDate),
                ...getPricingFieldsForSave(
                  campaignFromForm?.cpm || campaignData.cpm || 7,
                  selectedAdvertiser,
                  campaignFromForm?.type || campaignData.type || 1,
                  campaignFromForm?.advertiserPercentage
                ),
                type: campaignTypeMap[campaignFromForm?.type || campaignData.type || 1],
                hasMediaPlanStrategy: campaignFromForm?.hasMediaPlanStrategy ?? false,
                valueAdd: campaignFromForm?.valueAdd ?? false,
                demandSidePlatform: campaignFromForm?.demandSidePlatform || null,
                adProviderId: campaignFromForm?.adProviderId || null,
                maxBid: campaignFromForm?.maxBid ? Number(campaignFromForm.maxBid) : null,
                // Campaign-level flight schedules for new campaigns
                ...(() => {
                  const formFlights = campaignFromForm?.flightSchedule || [];
                  if (formFlights.length > 0) {
                    return {
                      flightSchedules: {
                        create: formFlights.map((f) => ({
                          startDate: normalizeStartDate(f.startDate),
                          endDate: normalizeEndDate(f.endDate),
                          budget: Number(f.budget) || 0,
                          cpm: Number(f.cpm) || 7,
                        })),
                      },
                    };
                  }
                  return {};
                })(),
                orgs: selectedAdvertiser ? {
                  connect: { id: selectedAdvertiser.id },
                } : undefined,
              }));
            }

            // Handle deletions
            if (campaignsToDelete.length > 0) {
              campaignsUpdatePayload.delete = campaignsToDelete.map(id => ({ id }));
            }

            // Only add campaigns field if there are operations to perform
            if (Object.keys(campaignsUpdatePayload).length > 0) {
              updateData.campaigns = campaignsUpdatePayload;
            }
          }

          // Actually save the campaign order metadata to database
          const { data: updateResult } = await saveWithRetry(updateCampaignOrder, {
            where: { id: campaignOrderId },
            data: updateData
          });

          // Map all campaign IDs by their index from the update result
          // This includes both existing campaigns and newly created campaigns
          if (updateResult?.updateCampaignOrderRequestor?.campaigns) {
            const updatedCampaigns = updateResult.updateCampaignOrderRequestor.campaigns;

            // Map campaigns from form data to result campaigns
            // We need to match by comparing names and other properties since newly created campaigns won't have IDs in the form
            campaignData.campaigns.forEach((formCampaign, formIndex) => {
              if (formCampaign.id) {
                // Existing campaign - find by ID
                const matchingCampaign = updatedCampaigns.find(c => c.id === formCampaign.id);
                if (matchingCampaign) {
                  campaignIdsByIndex[formIndex] = matchingCampaign.id;
                }
              } else {
                // New campaign - find by matching properties (name, budget, type)
                // New campaigns will be at the end of the array after existing ones
                const existingCount = campaignData.campaigns.filter(c => c.id).length;
                const newCampaignIndex = formIndex - existingCount;
                const newCampaigns = updatedCampaigns.filter(c => {
                  // Filter out campaigns that already existed (have IDs in form)
                  const existingIds = campaignData.campaigns.filter(fc => fc.id).map(fc => fc.id);
                  return !existingIds.includes(c.id);
                });

                if (newCampaigns[newCampaignIndex]) {
                  campaignIdsByIndex[formIndex] = newCampaigns[newCampaignIndex].id;
                }
              }
            });
          }

          // For backwards compatibility, set parentCampaignId to first campaign
          parentCampaignId = campaignIdsByIndex[0] || campaignData.campaigns?.[0]?.id || null;
        } else {
          // Create new campaign order
          // Step 1: Create campaign order WITHOUT campaigns field
          const createData = extractCampaignMetadata(campaignData, currentCampaignOrder, selectedAdvertiser);

          const { data: campaignResult } = await saveWithRetry(
            createCampaignOrder,
            { data: createData }
          );

          campaignOrderId = campaignResult.createCampaignOrderRequestor.id;

          // Step 2: Update campaign order with campaigns from campaignData.campaigns array
          // Map campaigns from the form data (not the order name/budget)
          const campaignsToCreate = (campaignData.campaigns || []).map((campaign) => ({
            name: campaign.name || `Campaign ${Date.now()}`,
            budgetTotal: Number(campaign.budget || 0),
            startDate: normalizeStartDate(campaign.startDate || campaignData.startDate),
            endDate: normalizeEndDate(campaign.endDate || campaignData.endDate),
            ...getPricingFieldsForSave(
              campaign.cpm || campaignData.cpm || 7,
              selectedAdvertiser,
              campaign.type || campaignData.type || 1,
              campaign.advertiserPercentage
            ),
            type: campaignTypeMap[campaign.type || campaignData.type || 1],
            hasMediaPlanStrategy: campaign?.hasMediaPlanStrategy ?? false,
            valueAdd: campaign?.valueAdd ?? false,
            demandSidePlatform: campaign?.demandSidePlatform || null,
            // Connect campaign to advertiser organization
            orgs: selectedAdvertiser ? {
              connect: { id: selectedAdvertiser.id },
            } : undefined,
            // Don't include line items here - they will be processed individually later
          }));

          // Only update with campaigns if we have campaigns to create
          if (campaignsToCreate.length > 0) {
            const { data: updateResult } = await saveWithRetry(updateCampaignOrder, {
              where: { id: campaignOrderId },
              data: {
                campaigns: {
                  create: campaignsToCreate
                }
              }
            });

            // Step 3: Map all campaign IDs by their index for multi-campaign support
            const createdCampaigns = updateResult.updateCampaignOrderRequestor.campaigns;
            createdCampaigns.forEach((campaign, index) => {
              campaignIdsByIndex[index] = campaign.id;
            });

            // For backwards compatibility, set parentCampaignId to first campaign
            parentCampaignId = createdCampaigns[0].id;
          } else {
            // If no campaigns provided, set parentCampaignId to null
            parentCampaignId = null;
          }
        }

        updateProgress({
          phase: 'Processing line items',
          currentOperation: `Campaign order created. Processing ${lineItems.length} line items...`,
          current: 1,
        });

        // Phase 2: Process Line Items Sequentially
        const processedLineItems = [];
        const failedLineItems = [];

        for (let i = 0; i < lineItems.length; i++) {
          // Check for cancellation
          if (abortController.current.signal.aborted) {
            throw new Error('Save operation cancelled');
          }

          // Check for pause
          while (saveProgress.isPaused) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          const lineItem = lineItems[i];
          const lineItemId = lineItem.id || `temp-${i}`;

          // Get the correct campaign ID for this line item based on its campaign index
          const lineItemCampaignIndex = lineItem._campaignIndex ?? 0; // Default to 0 for backwards compatibility
          const lineItemCampaignId = campaignIdsByIndex[lineItemCampaignIndex] || parentCampaignId;

          try {
            // Update status to processing
            updateLineItemStatus(lineItemId, 'processing');

            updateProgress({
              currentOperation: `Saving line item ${i + 1} of ${lineItems.length}: "${lineItem.name || 'Unnamed'}" (Campaign ${lineItemCampaignIndex + 1})`,
              current: i + 1,
            });

            // Prepare line item data with all required parameters from original
            // Get original line items from the correct campaign
            const originalLineItems = currentCampaignOrder?.campaigns?.[lineItemCampaignIndex]?.lineItems || [];

            // Determine if this is a create context based on whether we're doing a campaign update or create
            // For updates: use update operations even if lineItemCampaignId is null (forced null for relationship issues)
            // For creates: use create operations for line items without IDs
            const isCreateContext = !isUpdate && !lineItem.id;

            const lineItemData = prepareLineItemForIndividualSave(
              lineItem,
              lineItemCampaignId,
              selectedAdvertiser,
              originalLineItems,
              isCreateContext,
              currentCampaignOrder?.status
            );

            // For updates with existing lineItem IDs, use UPDATE context to enable targeting deletions
            // Only force CREATE for new line items (without IDs) to avoid relationship issues
            const shouldForceCreate = isUpdate && !lineItem.id; // Only create new line items during updates

            // Save individual line item using mutation
            // TODO: Replace with actual ADD_LINE_ITEM_TO_CAMPAIGN mutation when available
            try {
              // If lineItemCampaignId is null (forced for updates), skip the update attempt and go to alternative
              if (!lineItemCampaignId) {
                throw new Error('No parent campaign ID - creating new campaign');
              }

              await saveWithRetry(updateCampaignOrder, {
                where: { id: campaignOrderId },
                data: {
                  campaigns: {
                    update: {
                      where: { id: lineItemCampaignId },
                      data: {
                        lineItems: (lineItem.id && !shouldForceCreate) ? {
                          update: {
                            where: { id: lineItem.id },
                            data: lineItemData
                          }
                        } : {
                          create: [lineItemData]
                        }
                      }
                    }
                  }
                }
              });
            } catch (error) {
              console.error(`Campaign ${lineItemCampaignId} not found. Trying alternative approach...`);

              // Alternative approach: Try to create a new campaign with this line item
              // CRITICAL: Preserve the user's targeting changes (including deletions) in fallback
              // Instead of stripping targeting data, we need to preserve deletions in the correct context

              const createContextLineItemData = prepareLineItemForIndividualSave(
                lineItem, // Preserve the user's changes including deletions
                null, // no lineItemCampaignId = create context
                selectedAdvertiser,
                originalLineItems,
                true, // force create context
                currentCampaignOrder?.status
              );

              // For updates, check if we have existing campaign to update instead of always creating
              const existingCampaign = currentCampaignOrder?.campaigns?.[lineItemCampaignIndex];

              let alternativeResult;
              if (isUpdate && existingCampaign?.id) {
                // Use update path with proper line item create/update/delete operations

                alternativeResult = await saveWithRetry(updateCampaignOrder, {
                  where: { id: campaignOrderId },
                  data: {
                    campaigns: {
                      update: {
                        where: { id: existingCampaign.id },
                        data: {
                          lineItems: lineItem.id ? {
                            // Update existing line item
                            update: {
                              where: { id: lineItem.id },
                              data: lineItemData
                            }
                          } : {
                            // Create new line item
                            create: [createContextLineItemData]
                          }
                        }
                      }
                    }
                  }
                });

                // Update the campaign ID mapping
                campaignIdsByIndex[lineItemCampaignIndex] = existingCampaign.id;
              } else {
                // Use create path for new campaigns
                // Get campaign data for this specific campaign index
                const campaignFromForm = campaignData.campaigns?.[lineItemCampaignIndex];

                alternativeResult = await saveWithRetry(updateCampaignOrder, {
                  where: { id: campaignOrderId },
                  data: {
                    campaigns: {
                      create: [{
                        name: campaignFromForm?.name || `Campaign ${lineItemCampaignIndex + 1}`,
                        budgetTotal: Number(campaignFromForm?.budget || campaignData.budget || 0),
                        startDate: normalizeStartDate(campaignFromForm?.startDate || campaignData.startDate),
                        endDate: normalizeEndDate(campaignFromForm?.endDate || campaignData.endDate),
                        ...getPricingFieldsForSave(
                          campaignFromForm?.cpm || campaignData.cpm || 7,
                          selectedAdvertiser,
                          campaignFromForm?.type || campaignData.type || 1,
                          campaignFromForm?.advertiserPercentage
                        ),
                        type: campaignTypeMap[campaignFromForm?.type || campaignData.type || 1],
                        hasMediaPlanStrategy: campaignFromForm?.hasMediaPlanStrategy ?? false,
                        valueAdd: campaignFromForm?.valueAdd ?? false,
                        demandSidePlatform: campaignFromForm?.demandSidePlatform || null,
                        adProviderId: campaignFromForm?.adProviderId || null,
                        maxBid: campaignFromForm?.maxBid ? Number(campaignFromForm.maxBid) : null,
                        // Connect campaign to advertiser organization
                        orgs: selectedAdvertiser ? {
                          connect: { id: selectedAdvertiser.id },
                        } : undefined,
                        lineItems: {
                          create: [createContextLineItemData]
                        }
                      }]
                    }
                  }
                });

                // Update campaign ID mapping for the newly created campaign
                if (alternativeResult?.data?.updateCampaignOrderRequestor?.campaigns?.length > 0) {
                  const newCampaigns = alternativeResult.data.updateCampaignOrderRequestor.campaigns;
                  const newCampaignId = newCampaigns[newCampaigns.length - 1].id;
                  campaignIdsByIndex[lineItemCampaignIndex] = newCampaignId;
                }
              }
            }

            // Mark as completed
            updateLineItemStatus(lineItemId, 'completed');
            processedLineItems.push({ ...lineItem, index: i });

          } catch (error) {
            console.error(`Failed to save line item ${i + 1}:`, error);

            // Mark as failed
            updateLineItemStatus(lineItemId, 'failed', error.message);
            failedLineItems.push({ ...lineItem, index: i, error: error.message });

            // Continue with next line item (don't fail entire operation)
          }
        }

        // Phase 3: Complete
        const successCount = processedLineItems.length;
        const failureCount = failedLineItems.length;

        updateProgress({
          phase: 'Complete',
          currentOperation: `Campaign saved. ${successCount} line items successful, ${failureCount} failed.`,
          current: totalSteps,
          total: totalSteps,
          isActive: false,
        });

        if (failureCount > 0) {
          message.warning(`Campaign saved with ${failureCount} line item failures. Click "Retry Failed Items" to retry.`);
        } else {
          message.success('Campaign and all line items saved successfully!');
        }

        return {
          success: true,
          campaignOrderId,
          processedLineItems,
          failedLineItems,
          partialSuccess: failureCount > 0
        };

      } catch (error) {
        updateProgress({
          isActive: false,
          errors: [error.message],
        });

        message.error(`Campaign save failed: ${error.message}`);
        return { success: false, error };
      }
    },
    [updateProgress, extractCampaignMetadata, saveWithRetry, updateCampaignOrder, createCampaignOrder, saveProgress.isPaused, updateLineItemStatus, prepareLineItemForIndividualSave]
  );

  /**
   * Pause the ongoing save operation
   */
  const pauseSave = useCallback(() => {
    setSaveProgress(prev => ({
      ...prev,
      isPaused: true,
      currentOperation: 'Paused - Click Resume to continue',
    }));
    message.info('Save operation paused');
  }, []);

  /**
   * Resume the paused save operation
   */
  const resumeSave = useCallback(() => {
    setSaveProgress(prev => ({
      ...prev,
      isPaused: false,
      currentOperation: 'Resuming save operation...',
    }));
    message.info('Save operation resumed');
  }, []);

  /**
   * Cancel the ongoing save operation
   */
  const cancelSave = useCallback(() => {
    if (abortController.current) {
      abortController.current.abort();
      setSaveProgress({
        isActive: false,
        phase: 'Cancelled',
        current: 0,
        total: 0,
        percent: 0,
        errors: ['Operation cancelled by user'],
        currentOperation: '',
        estimatedTime: 0,
        lineItemStatus: [],
        failedLineItems: [],
        completedLineItems: [],
        currentLineItem: null,
        canRetry: false,
        isPaused: false,
      });
      message.warning('Save operation cancelled');
    }
  }, []);

  /**
   * Retry only the failed line items
   */
  const retryFailedLineItems = useCallback(async (campaignOrderId, parentCampaignId) => {
    const failedItems = saveProgress.failedLineItems;
    if (failedItems.length === 0) {
      message.info('No failed line items to retry');
      return;
    }

    updateProgress({
      isActive: true,
      phase: 'Retrying failed line items',
      currentOperation: `Retrying ${failedItems.length} failed line items...`,
      current: 0,
      total: failedItems.length,
      errors: [],
    });

    const retryResults = [];

    for (let i = 0; i < failedItems.length; i++) {
      const lineItem = failedItems[i];
      const lineItemId = lineItem.id || `temp-${lineItem.index}`;

      try {
        // Reset status to processing
        updateLineItemStatus(lineItemId, 'processing');

        updateProgress({
          currentOperation: `Retrying line item: "${lineItem.name || 'Unnamed'}" (${i + 1} of ${failedItems.length})`,
          current: i + 1,
        });

        // Prepare and save line item
        // For retries, we're creating new line items, so use create context
        // Pass null for status since we don't have access to currentCampaignOrder in retry context
        // The isOrigin value from the original lineItem will be preserved
        const lineItemData = prepareLineItemForIndividualSave(
          lineItem,
          parentCampaignId,
          null, // selectedAdvertiser
          [], // originalLineItems
          true, // isCreateContext - retries are creating new line items
          null // campaignOrderStatus - not available in retry context, but isOrigin is preserved from lineItem
        );

        await saveWithRetry(updateCampaignOrder, {
          where: { id: campaignOrderId },
          data: {
            campaigns: {
              update: {
                where: { id: parentCampaignId },
                data: {
                  lineItems: {
                    create: [lineItemData]
                  }
                }
              }
            }
          }
        });

        // Mark as completed
        updateLineItemStatus(lineItemId, 'completed');
        retryResults.push({ success: true, lineItem });

      } catch (error) {
        console.error(`Retry failed for line item ${lineItem.name}:`, error);
        updateLineItemStatus(lineItemId, 'failed', error.message);
        retryResults.push({ success: false, lineItem, error: error.message });
      }
    }

    const successfulRetries = retryResults.filter(r => r.success).length;
    const failedRetries = retryResults.filter(r => !r.success).length;

    updateProgress({
      phase: 'Retry Complete',
      currentOperation: `Retry completed: ${successfulRetries} successful, ${failedRetries} still failed`,
      current: failedItems.length,
      isActive: false,
    });

    if (failedRetries === 0) {
      message.success('All failed line items successfully saved!');
    } else {
      message.warning(`${successfulRetries} line items saved, ${failedRetries} still failed`);
    }

    return retryResults;
  }, [saveProgress.failedLineItems, updateProgress, updateLineItemStatus, prepareLineItemForIndividualSave, saveWithRetry, updateCampaignOrder]);

  return {
    // Main save function with sequential processing
    saveCampaign: saveCampaignSequentially,

    // Control functions
    cancelSave,
    pauseSave,
    resumeSave,
    retryFailedLineItems,

    // Progress and status
    saveProgress,
    isSaving: saveProgress.isActive,
    isPaused: saveProgress.isPaused,
    canRetry: saveProgress.canRetry,

    // Detailed status for UI
    lineItemStatus: saveProgress.lineItemStatus,
    failedLineItems: saveProgress.failedLineItems,
    completedLineItems: saveProgress.completedLineItems,
    currentLineItem: saveProgress.currentLineItem,
  };
};